import {
  SHARE_CASHBACK_CREDITS,
  SHARE_CASHBACK_RULES,
  type ShareCashbackOffer,
  type ShareEarningEntry,
  type ShareEarningKind,
  type ShareEarningStatus,
  type ShareLink,
  type ShareLinkChannel,
  type ShareLinksResponse,
} from '@pulsify/shared/dto'
import { Copy, QrCode } from 'lucide-react'
import { toast } from 'sonner'
import type { ShareLinkOwner } from '~/entities/share-link/api/share-links'
import { useShareLinks } from '~/entities/share-link/model/use-share-links'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { copyToClipboard } from '~/shared/lib/share'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'
import { Disclosure } from '~/shared/ui/disclosure'
import { InfoTooltip } from '~/shared/ui/info-tooltip'
import { QrDownload } from '~/shared/ui/qr-download'
import { Skeleton } from '~/shared/ui/skeleton'

const CHANNEL_LABELS: Record<ShareLinkChannel, () => string> = {
  general: m.shareChannelGeneral,
  instagram_bio: m.shareChannelInstagramBio,
  instagram_story: m.shareChannelInstagramStory,
  whatsapp: m.shareChannelWhatsapp,
  facebook: m.shareChannelFacebook,
  qr: m.shareChannelQr,
}

const SHARE_KIND_LABELS: Record<ShareEarningKind, () => string> = {
  visit: m.shareKindVisit,
  interest: m.shareKindInterest,
  signup: m.shareKindSignup,
  check_in: m.shareKindCheckIn,
  plan: m.shareKindPlan,
}

const STATUS_LABELS: Record<ShareEarningStatus, () => string> = {
  pending: m.shareStatusPending,
  confirmed: m.shareStatusConfirmed,
  held: m.shareStatusHeld,
  rejected: m.shareStatusRejected,
}

function useCount() {
  const format = new Intl.NumberFormat(getLocale())
  return (value: number) => format.format(value)
}

function offerEndLabel(offer: ShareCashbackOffer): string {
  return new Intl.DateTimeFormat(getLocale(), {
    timeZone: 'Europe/Vienna',
    day: 'numeric',
    month: 'numeric',
  }).format(new Date(new Date(offer.endsAt).getTime() - 1))
}

async function copyLink(url: string) {
  if (await copyToClipboard(url)) toast.success(m.linkCopied())
  else toast.error(m.failedToCopyLink())
}

function QrButton({ link, title }: { link: ShareLink; title: string }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={m.shareShowQr()} title={m.shareShowQr()}>
          <QrCode />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="break-all font-mono text-xs">{link.url}</DialogDescription>
        </DialogHeader>
        <QrDownload
          value={link.url}
          title={m.shareQrAlt({ title })}
          fileName={`pulsify-teilen-${link.code}`}
          downloadLabel={m.shareQrDownload()}
        />
      </DialogContent>
    </Dialog>
  )
}

function MainLink({ link }: { link: ShareLink }) {
  const label = CHANNEL_LABELS[link.channel]()
  return (
    <div className="flex items-center gap-2 rounded-lg border px-3 py-2">
      <p className="min-w-0 flex-1 truncate font-mono text-sm">
        {link.url.replace(/^https?:\/\//, '')}
      </p>
      <Button
        size="sm"
        onClick={() => copyLink(link.url)}
        aria-label={m.shareCopyLink({ channel: label })}
      >
        <Copy />
        {m.shareCopyShort()}
      </Button>
    </div>
  )
}

function LinkRow({ link, title }: { link: ShareLink; title: string }) {
  const count = useCount()
  const label = CHANNEL_LABELS[link.channel]()
  return (
    <li className="flex items-center gap-2 py-1.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">{label}</p>
        <p className="text-muted-foreground text-xs tabular-nums">
          {m.shareLinkVisits({ count: count(link.visitsThisMonth) })}
        </p>
      </div>
      {link.channel === 'qr' ? <QrButton link={link} title={title} /> : null}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={() => copyLink(link.url)}
        aria-label={m.shareCopyLink({ channel: label })}
        title={m.shareCopyLink({ channel: label })}
      >
        <Copy />
      </Button>
    </li>
  )
}

function History({ entries }: { entries: ShareEarningEntry[] }) {
  const locale = getLocale()
  return (
    <ul className="space-y-1">
      {entries.map((entry) => (
        <li key={entry.id} className="flex items-center gap-2 text-xs">
          <span className="w-20 shrink-0 text-muted-foreground tabular-nums">
            {new Date(entry.createdAt).toLocaleString(locale, {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <span className="min-w-0 flex-1 truncate">
            {SHARE_KIND_LABELS[entry.kind]()} · {CHANNEL_LABELS[entry.channel]()}
          </span>
          <span
            className={cn(
              'shrink-0 tabular-nums',
              entry.status === 'confirmed' ? 'text-brand' : 'text-muted-foreground',
            )}
          >
            {entry.credits > 0 ? `+${entry.credits} · ` : ''}
            {STATUS_LABELS[entry.status]()}
          </span>
        </li>
      ))}
    </ul>
  )
}

function OfferLines({ offer }: { offer: ShareCashbackOffer }) {
  const count = useCount()
  if (!offer.active) {
    return <p className="text-muted-foreground text-sm">{m.shareOfferOver()}</p>
  }
  return (
    <div className="space-y-0.5 text-sm">
      <p>
        {m.shareOfferRewards({
          visit: count(SHARE_CASHBACK_CREDITS.visit),
          signup: count(SHARE_CASHBACK_CREDITS.signup),
          plan: count(SHARE_CASHBACK_CREDITS.plan),
          checkIn: count(SHARE_CASHBACK_CREDITS.check_in),
        })}
      </p>
      <p className="text-muted-foreground">
        {m.shareOfferLimits({ date: offerEndLabel(offer), monthly: count(offer.monthlyCap) })}
      </p>
    </div>
  )
}

function ShareLinksBody({ data, title }: { data: ShareLinksResponse; title: string }) {
  const count = useCount()
  const [main, ...others] = data.links
  const earned = data.month.credits

  return (
    <>
      <OfferLines offer={data.offer} />
      {data.offer.active || earned > 0 ? (
        <p className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="font-semibold font-serif text-2xl text-brand tabular-nums">
            {count(earned)}
          </span>
          <span className="text-muted-foreground">
            {data.month.pendingCredits > 0
              ? m.shareMonthCreditsPending({ pending: count(data.month.pendingCredits) })
              : m.shareMonthCredits()}
          </span>
        </p>
      ) : null}
      {main ? <MainLink link={main} /> : null}
      {others.length > 0 ? (
        <Disclosure title={m.shareMoreLinks({ count: others.length })} contentClassName="space-y-3">
          <ul className="divide-y divide-border">
            {others.map((link) => (
              <LinkRow key={link.id} link={link} title={title} />
            ))}
          </ul>
          {data.history.length > 0 ? (
            <div className="space-y-1.5">
              <p className="font-medium text-sm">{m.shareHistoryTitle()}</p>
              <History entries={data.history} />
            </div>
          ) : null}
        </Disclosure>
      ) : null}
    </>
  )
}

export function ShareLinksCard({
  businessId,
  target,
  title,
}: {
  businessId: string
  target: ShareLinkOwner
  title: string
}) {
  const { data, isLoading, isError, refetch, isFetching } = useShareLinks(businessId, target)

  return (
    <Card className="flex w-full flex-col gap-3 p-4">
      <div className="flex items-center gap-1">
        <h3 className="font-semibold font-serif">{m.shareCardTitle()}</h3>
        <InfoTooltip label={m.shareCardTitle()}>
          {m.shareRulesDetail({
            hours: SHARE_CASHBACK_RULES.pendingHours,
            days: SHARE_CASHBACK_RULES.expiryDays,
          })}{' '}
          {m.shareRuleFairness()}
        </InfoTooltip>
      </div>
      {isLoading ? (
        <Skeleton className="h-32 w-full rounded-lg" />
      ) : isError || !data ? (
        <>
          <p className="text-destructive text-sm">{m.shareLoadFailed()}</p>
          <Button variant="outline" disabled={isFetching} onClick={() => refetch()}>
            {m.tryAgain()}
          </Button>
        </>
      ) : (
        <ShareLinksBody data={data} title={title} />
      )}
    </Card>
  )
}
